import { Navigate, Route, Routes, useLocation } from "react-router";
import { lazy, Suspense, type ReactNode } from "react";
import { podePedidoManual } from "./lib/pedidosManuais";
import { type Papel, temPapel, useAuth } from "./lib/auth";
import { configurado } from "./lib/supabase";
import { Moldura } from "./componentes/Topo";
import { itemDaRota, telaVisivel } from "./lib/menu";
import { Carregando } from "./componentes/ui";
import { SemConexao } from "./componentes/SemConexao";
import Login from "./paginas/Login";
import Inicio from "./paginas/Inicio";
import Separacao from "./paginas/separacao/Separacao";
import Conferencia from "./paginas/conferencia/Conferencia";
import Montagem from "./paginas/montagem/Montagem";
const Painel = lazy(() => import("./paginas/painel/Painel"));
const Divergencias = lazy(() => import("./paginas/painel/Divergencias"));
const Desempenho = lazy(() => import("./paginas/painel/Desempenho"));
const Pedidos = lazy(() => import("./paginas/painel/Pedidos"));
import Estoque from "./paginas/estoque/Estoque";
import Inventario from "./paginas/estoque/Inventario";
import Entrada from "./paginas/estoque/Entrada";
import Transferir from "./paginas/estoque/Transferir";
import Reposicao from "./paginas/estoque/Reposicao";
import Contagem from "./paginas/estoque/Contagem";
const Inventarios = lazy(() => import("./paginas/estoque/Inventarios"));
const InventarioDetalhe = lazy(() => import("./paginas/estoque/InventarioDetalhe"));
const Ocupacao = lazy(() => import("./paginas/estoque/Ocupacao"));
const Canais = lazy(() => import("./paginas/estoque/Canais"));
const AdicionarSku = lazy(() => import("./paginas/estoque/AdicionarSku"));
const MapaCalor = lazy(() => import("./paginas/estoque/MapaCalor"));
const Minimos = lazy(() => import("./paginas/cadastros/Minimos"));
const Embalagens = lazy(() => import("./paginas/cadastros/Embalagens"));
const Locais = lazy(() => import("./paginas/cadastros/Locais"));
const Produtos = lazy(() => import("./paginas/cadastros/Produtos"));
const Lotes = lazy(() => import("./paginas/lotes/Lotes"));
const CargaInicial = lazy(() => import("./paginas/cadastros/CargaInicial"));
const Usuarios = lazy(() => import("./paginas/admin/Usuarios"));
const Integracao = lazy(() => import("./paginas/admin/Integracao"));
const Configuracoes = lazy(() => import("./paginas/admin/Configuracoes"));
const ModelosEtiquetas = lazy(() => import("./paginas/admin/ModelosEtiquetas"));
const Registros = lazy(() => import("./paginas/admin/Registros"));
const Expedicao = lazy(() => import("./paginas/expedicao/Expedicao"));
const Romaneio = lazy(() => import("./paginas/expedicao/Romaneio"));
const RomaneioImprimir = lazy(() => import("./paginas/expedicao/RomaneioImprimir"));
const ConsultaEnvio = lazy(() => import("./paginas/expedicao/ConsultaEnvio"));
const ComprovanteEnvio = lazy(() => import("./paginas/expedicao/ComprovanteEnvio"));
const AgendaColetas = lazy(() => import("./paginas/expedicao/AgendaColetas"));
const Recebimento = lazy(() => import("./paginas/recebimento/Recebimento"));
const RecebimentoDetalhe = lazy(() => import("./paginas/recebimento/RecebimentoDetalhe"));
import Armazenar from "./paginas/recebimento/Armazenar";
const AgendaRecebimento = lazy(() => import("./paginas/recebimento/Agenda"));
const Devolucoes = lazy(() => import("./paginas/devolucoes/Devolucoes"));
const DevolucaoDetalhe = lazy(() => import("./paginas/devolucoes/DevolucaoDetalhe"));
import ReceberDevolucao from "./paginas/devolucoes/ReceberDevolucao";
const Producao = lazy(() => import("./paginas/producao/Producao"));
const ProducaoDetalhe = lazy(() => import("./paginas/producao/ProducaoDetalhe"));
const Full = lazy(() => import("./paginas/full/Full"));
const FullDetalhe = lazy(() => import("./paginas/full/FullDetalhe"));

// telas de chão carregam junto; painel, cadastros e admin vêm sob demanda (coletora abre mais rápido)
function Protegida({ papeis, children }: { papeis: Papel[]; children: ReactNode }) {
  const { perfil } = useAuth();
  const { pathname } = useLocation();
  const tela = itemDaRota(pathname);
  if (!temPapel(perfil, ...papeis) && !(tela?.to === "/pedidos" && podePedidoManual(perfil, "consultar"))) return <Navigate to="/" replace />;
  // tela escondida pela função (Usuários, aba Funções)
  if (tela && !telaVisivel(perfil, tela.to)) return <Navigate to="/" replace />;
  return <Suspense fallback={<Carregando />}>{children}</Suspense>;
}

export default function App() {
  const { sessao, perfil, carregando, semConexao, sair } = useAuth();

  if (!configurado) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <h1 className="font-sinal text-3xl font-bold">Falta configurar o Supabase</h1>
        <p className="mt-3">Crie o arquivo <code>web/.env.local</code> a partir do <code>.env.example</code> com a URL e a chave pública do projeto.</p>
      </div>
    );
  }
  if (carregando) return semConexao ? <SemConexao /> : <Carregando />;
  if (!sessao) return <Login />;
  if (!perfil || !perfil.ativo) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <h1 className="font-sinal text-3xl font-bold">Usuário sem acesso ao WMS</h1>
        <p className="mt-3">Peça para um admin cadastrar ou reativar o seu perfil.</p>
        <button type="button" className="mt-6 min-h-11 rounded-md bg-tinta px-5 font-semibold text-white" onClick={() => void sair()}>Sair</button>
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<Moldura />}>
        <Route index element={<Inicio />} />
        <Route path="separacao" element={<Protegida papeis={["separador", "lider"]}><Separacao /></Protegida>} />
        <Route path="conferencia" element={<Protegida papeis={["conferente", "lider"]}><Conferencia /></Protegida>} />
        <Route path="montagem" element={<Protegida papeis={["separador", "lider"]}><Montagem /></Protegida>} />
        <Route path="painel" element={<Protegida papeis={["lider"]}><Painel /></Protegida>} />
        <Route path="painel/divergencias" element={<Protegida papeis={["lider"]}><Divergencias /></Protegida>} />
        <Route path="painel/desempenho" element={<Protegida papeis={["lider"]}><Desempenho /></Protegida>} />
        <Route path="pedidos" element={<Protegida papeis={["lider", "conferente", "expedidor", "cx"]}><Pedidos /></Protegida>} />
        <Route path="expedicao" element={<Protegida papeis={["expedidor", "lider", "cx"]}><Expedicao /></Protegida>} />
        <Route path="expedicao/agenda" element={<Protegida papeis={["expedidor", "lider", "cx"]}><AgendaColetas /></Protegida>} />
        <Route path="expedicao/consulta" element={<Protegida papeis={["expedidor", "lider", "cx"]}><ConsultaEnvio /></Protegida>} />
        <Route path="expedicao/consulta/:pedidoId/comprovante" element={<Protegida papeis={["expedidor", "lider", "cx"]}><ComprovanteEnvio /></Protegida>} />
        <Route path="expedicao/:id" element={<Protegida papeis={["expedidor", "lider", "cx"]}><Romaneio /></Protegida>} />
        <Route path="expedicao/:id/imprimir" element={<Protegida papeis={["expedidor", "lider", "cx"]}><RomaneioImprimir /></Protegida>} />
        <Route path="recebimento" element={<Protegida papeis={["estoquista", "lider"]}><Recebimento /></Protegida>} />
        <Route path="recebimento/armazenar" element={<Protegida papeis={["estoquista", "lider"]}><Armazenar /></Protegida>} />
        <Route path="recebimento/agenda" element={<Protegida papeis={["estoquista", "lider"]}><AgendaRecebimento /></Protegida>} />
        <Route path="recebimento/:id" element={<Protegida papeis={["estoquista", "lider"]}><RecebimentoDetalhe /></Protegida>} />
        <Route path="devolucoes" element={<Protegida papeis={["estoquista", "lider", "cx"]}><Devolucoes /></Protegida>} />
        <Route path="devolucoes/receber" element={<Protegida papeis={["estoquista", "lider"]}><ReceberDevolucao /></Protegida>} />
        <Route path="devolucoes/:id" element={<Protegida papeis={["estoquista", "lider", "cx"]}><DevolucaoDetalhe /></Protegida>} />
        <Route path="producao" element={<Protegida papeis={["separador", "estoquista", "lider"]}><Producao /></Protegida>} />
        <Route path="producao/:id" element={<Protegida papeis={["separador", "estoquista", "lider"]}><ProducaoDetalhe /></Protegida>} />
        <Route path="full" element={<Protegida papeis={["separador", "conferente", "expedidor", "lider"]}><Full /></Protegida>} />
        <Route path="full/:id" element={<Protegida papeis={["separador", "conferente", "expedidor", "lider"]}><FullDetalhe /></Protegida>} />
        <Route path="estoque" element={<Protegida papeis={["lider", "estoquista", "separador"]}><Estoque /></Protegida>} />
        <Route path="estoque/inventario" element={<Protegida papeis={["lider", "estoquista"]}><Inventario /></Protegida>} />
        <Route path="estoque/entrada" element={<Protegida papeis={["lider", "estoquista"]}><Entrada /></Protegida>} />
        <Route path="estoque/adicionar" element={<Protegida papeis={["lider", "estoquista"]}><AdicionarSku /></Protegida>} />
        <Route path="estoque/transferir" element={<Protegida papeis={["lider", "estoquista", "separador"]}><Transferir /></Protegida>} />
        <Route path="estoque/reposicao" element={<Protegida papeis={["lider", "estoquista"]}><Reposicao /></Protegida>} />
        <Route path="estoque/contagem" element={<Protegida papeis={["lider", "estoquista"]}><Contagem /></Protegida>} />
        <Route path="estoque/ocupacao" element={<Protegida papeis={["lider", "estoquista"]}><Ocupacao /></Protegida>} />
        <Route path="estoque/canais" element={<Protegida papeis={["lider", "estoquista"]}><Canais /></Protegida>} />
        <Route path="estoque/mapa-calor" element={<Protegida papeis={["lider"]}><MapaCalor /></Protegida>} />
        <Route path="inventarios" element={<Protegida papeis={["lider"]}><Inventarios /></Protegida>} />
        <Route path="inventarios/:id" element={<Protegida papeis={["lider"]}><InventarioDetalhe /></Protegida>} />
        <Route path="cadastros/reposicao" element={<Protegida papeis={["lider"]}><Minimos /></Protegida>} />
        <Route path="cadastros/embalagens" element={<Protegida papeis={["lider"]}><Embalagens /></Protegida>} />
        <Route path="cadastros/locais" element={<Protegida papeis={["lider"]}><Locais /></Protegida>} />
        <Route path="cadastros/produtos" element={<Protegida papeis={["lider"]}><Produtos /></Protegida>} />
        <Route path="cadastros/lotes" element={<Protegida papeis={["lider"]}><Lotes /></Protegida>} />
        <Route path="cadastros/carga" element={<Protegida papeis={["lider"]}><CargaInicial /></Protegida>} />
        <Route path="admin/usuarios" element={<Protegida papeis={["admin"]}><Usuarios /></Protegida>} />
        <Route path="admin/integracao" element={<Protegida papeis={["admin"]}><Integracao /></Protegida>} />
        <Route path="admin/configuracoes" element={<Protegida papeis={["admin", "lider"]}><Configuracoes /></Protegida>} />
        <Route path="admin/configuracoes/etiquetas" element={<Protegida papeis={["admin"]}><ModelosEtiquetas /></Protegida>} />
        <Route path="admin/registros" element={<Protegida papeis={["admin"]}><Registros /></Protegida>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
